import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendEmailVerification,
  User,
  reload,
  verifyBeforeUpdateEmail,
  reauthenticateWithPopup,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  deleteUser,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithCredential,
  linkWithPopup,
  linkWithCredential,
  unlink
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { auth, db } from '../lib/firebase';
import { prefetchUserData } from '../lib/prefetch';
import toast from 'react-hot-toast';
import { doc, setDoc, getDoc, getDocFromCache, collection, query, where, getDocs, writeBatch, deleteDoc, deleteField } from 'firebase/firestore';
import { INVITE_SYSTEM_ENABLED } from '../config';
import { validateAndUseInviteCode } from '../services/inviteService';
import { ConsentData, CONSENT_CONFIG, setConsentByEmail } from '../services/consentService';
import { UserDisplayPreferences, DEFAULT_DISPLAY_PREFERENCES } from '../types/userPreferences';
import { debug } from '../utils/debug';
import { setUserAnalyticsId } from '../services/analyticsService';
import { Info } from 'lucide-react';

export interface UserLoyaltyStatus {
  cruiseLineName: string;
  status: number;
  loyaltyNumber?: string;
  grandfatheredTier?: 'Diamond' | 'Platinum' | 'Gold' | 'Red' | string;
  grandfatheredExpiry?: 'lifetime' | '2028-05-31' | '2028-12-31' | string;
  legacyPoints?: number;
  cycleEndDate?: string;
}

interface AuthContextType {
  currentUser: User | null;
  isAdmin: boolean;
  activeSignInProvider: string | null;
  loading: boolean;
  signup: (email: string, password: string, displayName: string, inviteCode?: string, consentData?: ConsentData) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithApple: () => Promise<void>;
  linkGoogleAccount: () => Promise<void>;
  linkAppleAccount: () => Promise<void>;
  unlinkAuthProvider: (providerId: string) => Promise<void>;
  logout: () => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  reloadUser: () => Promise<void>;
  updateUserDisplayName: (displayName: string, suppressToast?: boolean) => Promise<void>;
  updateUserEmail: (newEmail: string, currentPassword?: string) => Promise<void>;
  updateUserPassword: (currentPassword: string, newPassword: string) => Promise<void>;
  updateUserPreferredDisplayName: (preferredDisplayName: string | null, suppressToast?: boolean) => Promise<void>;
  updateUserLoyaltyStatus: (loyaltyStatus: UserLoyaltyStatus[]) => Promise<void>;
  updateUserConsent: (consentData: ConsentData) => Promise<void>;
  updateUserDisplayPreferences: (displayPreferences: Partial<UserDisplayPreferences>) => Promise<void>;
  deleteUserAccount: (currentPassword: string) => Promise<void>;
  pendingEmail: string | null;
  cancelPendingEmailUpdate: () => Promise<void>;
  verifyAndRefreshEmailUpdate: () => Promise<void>;
  preferredDisplayName: string | null | undefined;
  loyaltyStatus: UserLoyaltyStatus[];
  userConsent: ConsentData | null;
  displayPreferences: UserDisplayPreferences;
  consentRequired: boolean;
  clearConsentRequired: () => void;
}

// Delay before retrying user doc fetch when doc is missing (allows signup setDoc to complete)
const USER_DOC_RETRY_DELAY_MS = 600;

// Timeout before falling back to defaults when server is slow (poor internet)
const USER_DOC_TIMEOUT_MS = 2500;

// Timeout for Firebase Auth to restore the persisted session via onAuthStateChanged.
// On poor networks (e.g. cruise ship WiFi), Firebase Auth can stall 5-10+ seconds before
// firing. If this timeout fires first, the app unblocks with safe defaults so the user
// can interact (e.g. tap Login) rather than staring at the splash screen indefinitely.
// When onAuthStateChanged later resolves, state is silently updated.
// Total experience: 3s splash (SPLASH_MAX_DURATION_MS in InitialRouteHandler) + 2s skeleton = 5s max.
const AUTH_STATE_TIMEOUT_MS = 5000;

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const isUserCancellationError = (error: any): boolean => {
  if (!error) return false;
  const code = error.code || '';
  const message = (error.message || '').toLowerCase();
  return (
    code === 'auth/popup-closed-by-user' ||
    code === 'auth/cancelled-popup-request' ||
    code === 'auth/user-cancelled' ||
    code === 'auth/cancelled' ||
    message.includes('cancelled') ||
    message.includes('canceled') ||
    message.includes('popup-closed-by-user') ||
    message.includes('closed by user') ||
    message.includes('error 1001') ||
    message.includes('authorizationerror error 1001')
  );
};

export const formatAuthErrorMessage = (error: any): string => {
  if (!error) return 'An error occurred during authentication.';
  const msg = error.message || (typeof error === 'string' ? error : '');
  if (msg.includes('1000') || msg.includes('AuthenticationServices') || msg.includes('AuthorizationError')) {
    return 'Apple Sign-In timed out or was unable to connect to Apple services. Please ensure your Apple ID is signed in under device Settings and try again.';
  }
  if (error.code === 'auth/missing-or-invalid-nonce') {
    return 'Authentication session expired. Please try signing in again.';
  }
  if (error.code === 'auth/network-request-failed') {
    return 'Network connection error. Please check your internet connection.';
  }
  return msg;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [activeSignInProvider, setActiveSignInProvider] = useState<string | null>(null);
  const [preferredDisplayName, setPreferredDisplayName] = useState<string | null | undefined>(undefined);
  const [loyaltyStatus, setLoyaltyStatus] = useState<UserLoyaltyStatus[]>([]);
  const [userConsent, setUserConsent] = useState<ConsentData | null>(null);
  const [displayPreferences, setDisplayPreferences] = useState<UserDisplayPreferences>(DEFAULT_DISPLAY_PREFERENCES);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [consentRequired, setConsentRequired] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const checkAndApplyEmailVerification = async (user: User, userData: any) => {
    const verifiedAuthEmail = user.email ? user.email.toLowerCase().trim() : null;
    if (!verifiedAuthEmail) return userData;

    const isSSOUser = user.providerData.some(p => p.providerId === 'google.com' || p.providerId === 'apple.com');
    const isAuthVerified = user.emailVerified || isSSOUser;
    const isRelayEmail = verifiedAuthEmail.endsWith('privaterelay.appleid.com');

    // Register friend lookup for primary email if verified and not an Apple private relay email
    if (isAuthVerified && !isRelayEmail) {
      await setDoc(doc(db, 'friendLookup', verifiedAuthEmail), { uid: user.uid, email: verifiedAuthEmail }, { merge: true }).catch(() => {});
    }

    const oldEmailInDoc = userData.email ? userData.email.toLowerCase().trim() : null;
    const pendingEmailInDoc = userData.pendingEmail ? userData.pendingEmail.toLowerCase().trim() : null;

    if (pendingEmailInDoc && (verifiedAuthEmail === pendingEmailInDoc || (oldEmailInDoc && verifiedAuthEmail !== oldEmailInDoc))) {
      const updatedConsent = userData.consentData ? { ...userData.consentData, email: verifiedAuthEmail } : userConsent;
      const userPayload: any = {
        email: verifiedAuthEmail,
        pendingEmail: deleteField()
      };
      if (updatedConsent) {
        userPayload.consentData = updatedConsent;
        setUserConsent(updatedConsent);
        setConsentByEmail(verifiedAuthEmail, updatedConsent);
      }
      
      await setDoc(doc(db, 'users', user.uid), userPayload, { merge: true }).catch(() => {});
      if (!isRelayEmail) {
        await setDoc(doc(db, 'friendLookup', verifiedAuthEmail), { uid: user.uid, email: verifiedAuthEmail }, { merge: true }).catch(() => {});
      }
      
      if (oldEmailInDoc && oldEmailInDoc !== verifiedAuthEmail) {
        await deleteDoc(doc(db, 'friendLookup', oldEmailInDoc)).catch(() => {});
      }
      
      setPendingEmail(null);
      toast.success('Email update verified successfully!');
      return {
        ...userData,
        email: verifiedAuthEmail,
        pendingEmail: undefined,
        consentData: updatedConsent
      };
    } else if (pendingEmailInDoc) {
      setPendingEmail(pendingEmailInDoc);
    } else {
      setPendingEmail(null);
    }
    return userData;
  };

  useEffect(() => {
    // Track whether onAuthStateChanged has fired. Used by the auth-state timeout below.
    let authResolved = false;
    // Set to true if the timeout fires before onAuthStateChanged, so we can handle
    // the late-resolve case (auth fires 1-2s after timeout) gracefully.
    let authTimedOut = false;

    // Auth-state timeout: if Firebase Auth hasn't restored the session within
    // AUTH_STATE_TIMEOUT_MS, unblock the app with safe defaults.
    // This prevents the app from hanging indefinitely on poor/no network.
    const authTimeoutId = setTimeout(() => {
      if (!authResolved) {
        authTimedOut = true;
        debug.warn('[AuthContext] Auth state timeout reached — unblocking app with defaults');
        setCurrentUser(null);
        setActiveSignInProvider(null);
        setPreferredDisplayName(null);
        setLoyaltyStatus([]);
        setUserConsent(null);
        setConsentRequired(false);
        setDisplayPreferences(DEFAULT_DISPLAY_PREFERENCES);
        setIsAdmin(false);
        setLoading(false);
      }
    }, AUTH_STATE_TIMEOUT_MS);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      authResolved = true;
      clearTimeout(authTimeoutId);

      // If the timeout already fired (app is showing login screen) but auth resolves
      // with a real user, briefly set loading=true so downstream contexts (CruiseContext,
      // FriendContext) re-initialize against the real user rather than staying torn-down.
      if (authTimedOut && user) {
        debug.log('[AuthContext] Auth resolved after timeout — re-initializing with real user');
        setLoading(true);
      }
      debug.log('[AuthContext] onAuthStateChanged triggered', { 
        hasUser: !!user, 
        userEmail: user?.email,
        userDisplayName: user?.displayName,
        emailVerified: user?.emailVerified
      });
      
      if (user) {
        // Set currentUser immediately so app can render (avoids hang on poor internet).
        // Token refresh runs in background - cached token is valid; Firestore will use it.
        setPreferredDisplayName(undefined);
        debug.log('[AuthContext] Reset preferredDisplayName to undefined for new user');
        setCurrentUser(user);
        setUserAnalyticsId(user.uid);

        user.getIdTokenResult().then(tokenResult => {
          const provider = tokenResult.signInProvider || user.providerData[0]?.providerId || 'password';
          setActiveSignInProvider(provider);
        }).catch(() => {
          setActiveSignInProvider(user.providerData[0]?.providerId || 'password');
        });
        
        // Refresh ID token in background (non-blocking). Ensures Firestore rules see
        // email_verified when token was stale. Cached token works until refresh completes.
        if (user.emailVerified) {
          user.getIdToken(true)
            .then(() => debug.log('[AuthContext] ID token refreshed (background)'))
            .catch((err) => debug.warn('[AuthContext] Failed to refresh ID token:', err));
        }
        debug.log('[AuthContext] setCurrentUser called', { 
          email: user?.email,
          displayName: user?.displayName 
        });
        
        // Phase 1: Try cache immediately (fastest path - ~50-200ms)
        // This allows the app to render immediately with cached data
        const userDocRef = doc(db, 'users', user.uid);
        let cacheFound = false;
        let hasSetLoading = false;
        
        try {
          const cachedUserDoc = await getDocFromCache(userDocRef);
          if (cachedUserDoc.exists()) {
            const userData = cachedUserDoc.data() as any;
            const firestorePreferredName = userData.preferredDisplayName || null;
            debug.log('[AuthContext] Loaded from cache immediately (~50-200ms)', { 
              preferredDisplayName: firestorePreferredName,
              hasPreferredName: !!userData.preferredDisplayName
            });
            
            // Set to null if doesn't exist (checked but not set), not undefined
            setPreferredDisplayName(firestorePreferredName);
            setIsAdmin(userData.isAdmin === true);
            setLoyaltyStatus(userData.loyaltyStatus || []);
            const storedConsent = userData.consentData || null;
            setUserConsent(storedConsent);
            
            // Check if consent needs to be updated (version mismatch or missing)
            if (!storedConsent || !storedConsent.consented || storedConsent.version !== CONSENT_CONFIG.currentVersion) {
              debug.log('[AuthContext] Consent version check failed', {
                hasConsent: !!storedConsent,
                consented: storedConsent?.consented,
                storedVersion: storedConsent?.version,
                currentVersion: CONSENT_CONFIG.currentVersion
              });
              setConsentRequired(true);
            } else {
              setConsentRequired(false);
            }
            
            // Load display preferences, merging with defaults for any missing fields
            setDisplayPreferences({
              ...DEFAULT_DISPLAY_PREFERENCES,
              ...(userData.displayPreferences || {})
            });
            
            // Set loading to false immediately after cache load
            setLoading(false);
            hasSetLoading = true;
            cacheFound = true;
            debug.log('[AuthContext] Set loading to false (cache)');
          } else {
            // Cache returned but document doesn't exist - proceed to server fetch
            debug.debug('[AuthContext] Cache returned but document does not exist, fetching from server');
          }
        } catch {
          // No cache available yet - that's okay, will fetch from server
          debug.debug('[AuthContext] No cache available for immediate load');
        }
        
        // Phase 2: Timeout fallback - if server is slow (poor internet), proceed with defaults
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        const setupTimeoutFallback = () => {
          timeoutId = setTimeout(async () => {
            if (!hasSetLoading) {
              debug.debug('[AuthContext] Timeout reached, trying cache fallback');
              try {
                const cachedUserDoc = await getDocFromCache(userDocRef);
                if (cachedUserDoc.exists()) {
                  const userData = cachedUserDoc.data() as any;
                  setPreferredDisplayName(userData.preferredDisplayName || null);
                  setIsAdmin(userData.isAdmin === true);
                  setLoyaltyStatus(userData.loyaltyStatus || []);
                  const storedConsent = userData.consentData || null;
                  setUserConsent(storedConsent);
                  setConsentRequired(
                    !storedConsent ||
                    !storedConsent.consented ||
                    storedConsent.version !== CONSENT_CONFIG.currentVersion
                  );
                  setDisplayPreferences({
                    ...DEFAULT_DISPLAY_PREFERENCES,
                    ...(userData.displayPreferences || {}),
                  });
                  setLoading(false);
                  hasSetLoading = true;
                  debug.log('[AuthContext] Set loading to false (timeout cache fallback)');
                  return;
                }
              } catch {
                // No cache - proceed with defaults
              }
              // No cache available - use defaults so app can proceed
              setPreferredDisplayName(null);
              setIsAdmin(false);
              setLoyaltyStatus([]);
              setUserConsent(null);
              setConsentRequired(true);
              setDisplayPreferences(DEFAULT_DISPLAY_PREFERENCES);
              setLoading(false);
              hasSetLoading = true;
              debug.log('[AuthContext] Set loading to false (timeout defaults fallback)');
            }
          }, USER_DOC_TIMEOUT_MS);
        };
        if (!cacheFound) {
          setupTimeoutFallback();
        }
        
        // Phase 3: Always fetch from server in background to get latest data
        // This updates the UI silently when server responds
        getDoc(userDocRef).then(async (userDoc) => {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          if (userDoc.exists()) {
            let userData = userDoc.data() as any;
            userData = await checkAndApplyEmailVerification(user, userData);
            const firestorePreferredName = userData.preferredDisplayName || null;
            
            if (!cacheFound) {
              // No cache was found, this is the first load from server
              debug.log('[AuthContext] Firestore data fetched from server', { 
                preferredDisplayName: firestorePreferredName,
                hasPreferredName: !!userData.preferredDisplayName
              });
            } else {
              // Cache was found, this is a silent background refresh
              debug.debug('[AuthContext] Silent refresh completed from server');
            }
            
            // Always update state - React will handle efficient re-renders
            setPreferredDisplayName(firestorePreferredName);
            setIsAdmin(userData.isAdmin === true);
            setLoyaltyStatus(userData.loyaltyStatus || []);
            const storedConsent = userData.consentData || null;
            setUserConsent(storedConsent);
            
            // Check if consent needs to be updated
            if (!storedConsent || !storedConsent.consented || storedConsent.version !== CONSENT_CONFIG.currentVersion) {
              setConsentRequired(true);
            } else {
              setConsentRequired(false);
            }
            
            // Load display preferences
            setDisplayPreferences({
              ...DEFAULT_DISPLAY_PREFERENCES,
              ...(userData.displayPreferences || {})
            });
            
            if (!hasSetLoading) {
              setLoading(false);
              debug.log('[AuthContext] Set loading to false (server)');
            }
          } else {
            // User doc doesn't exist yet - may be signup race (setDoc not completed yet)
            if (!cacheFound) {
              debug.log('[AuthContext] User doc not found, retrying once after delay (signup race)');
              setTimeout(() => {
                getDoc(userDocRef).then(async (retryDoc) => {
                  if (retryDoc.exists()) {
                    let userData = retryDoc.data() as any;
                    userData = await checkAndApplyEmailVerification(user, userData);
                    const firestorePreferredName = userData.preferredDisplayName || null;
                    debug.log('[AuthContext] User doc found on retry', { preferredDisplayName: firestorePreferredName });
                    setPreferredDisplayName(firestorePreferredName);
                    setIsAdmin(userData.isAdmin === true);
                    setLoyaltyStatus(userData.loyaltyStatus || []);
                    const storedConsent = userData.consentData || null;
                    setUserConsent(storedConsent);
                    if (!storedConsent || !storedConsent.consented || storedConsent.version !== CONSENT_CONFIG.currentVersion) {
                      setConsentRequired(true);
                    } else {
                      setConsentRequired(false);
                    }
                    setDisplayPreferences({
                      ...DEFAULT_DISPLAY_PREFERENCES,
                      ...(userData.displayPreferences || {})
                    });
                    if (!hasSetLoading) {
                      setLoading(false);
                      debug.log('[AuthContext] Set loading to false (retry found doc)');
                    }
                  } else {
                    debug.log('[AuthContext] Firestore doc does not exist after retry');
                    setPreferredDisplayName(null);
                    setIsAdmin(false);
                    setLoyaltyStatus([]);
                    setUserConsent(null);
                    setConsentRequired(true);
                    setDisplayPreferences(DEFAULT_DISPLAY_PREFERENCES);
                    if (!hasSetLoading) {
                      setLoading(false);
                      debug.log('[AuthContext] Set loading to false (no doc after retry)');
                    }
                  }
                }).catch((retryError) => {
                  console.error('[AuthContext] Error fetching user document on retry:', retryError);
                  setPreferredDisplayName(null);
                  setIsAdmin(false);
                  setLoyaltyStatus([]);
                  setUserConsent(null);
                  setConsentRequired(true);
                  setDisplayPreferences(DEFAULT_DISPLAY_PREFERENCES);
                  if (!hasSetLoading) {
                    setLoading(false);
                    debug.log('[AuthContext] Set loading to false (retry error)');
                  }
                });
              }, USER_DOC_RETRY_DELAY_MS);
            } else {
              if (!hasSetLoading) {
                setLoading(false);
                debug.log('[AuthContext] Set loading to false (no doc)');
              }
            }
          }
        }).catch((error) => {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          console.error('[AuthContext] Error fetching user document:', error);
          if (!hasSetLoading) {
            setLoading(false);
            debug.log('[AuthContext] Set loading to false (error)');
          }
        });
      } else {
        // No user, set all to null/empty and mark as loaded
        debug.log('[AuthContext] No user, setting preferredDisplayName to null and loading to false');
        setCurrentUser(null);
        setUserAnalyticsId(null);
        setPreferredDisplayName(null);
        setIsAdmin(false);
        setLoyaltyStatus([]);
        setUserConsent(null);
        setConsentRequired(false);
        setDisplayPreferences(DEFAULT_DISPLAY_PREFERENCES);
        setLoading(false);
      }
    });

    return () => {
      clearTimeout(authTimeoutId);
      unsubscribe();
    };
  }, []);



  const signup = async (email: string, password: string, displayName: string, inviteCode?: string, consentData?: ConsentData) => {
    try {
      // Validate consent data
      if (!consentData || !consentData.consented) {
        throw new Error('Consent is required to create an account');
      }

      // If invite system is enabled, validate the invite code first
      if (INVITE_SYSTEM_ENABLED) {
        if (!inviteCode) {
          throw new Error('Invite code is required during beta testing.');
        }

        // Check if the invite code is valid before creating the account
        try {
          const { checkInviteCodeValidity } = await import('../services/inviteService');
          const isValid = await checkInviteCodeValidity(inviteCode);
          if (!isValid) {
            throw new Error('Invalid or already used invite code.');
          }
        } catch (error: any) {
          throw new Error(error.message || 'Invalid invite code.');
        }
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, { displayName });
      
      // If invite system is enabled, mark the invite code as used
      if (INVITE_SYSTEM_ENABLED && inviteCode) {
        try {
          await validateAndUseInviteCode(inviteCode, userCredential.user.uid);
        } catch (error) {
          // If invite code validation fails after account creation, we should handle this
          console.error('Failed to mark invite code as used:', error);
          // Note: Account was already created, so we don't want to fail here
        }
      }
      
      // Create user document in Firestore with consent data (highly reliable)
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: email.toLowerCase(), // Store email for friend requests
        preferredDisplayName: null,
        loyaltyStatus: [],
        inviteCodeUsed: inviteCode || null,
        consentData: consentData
      });

      // Send email verification (non-blocking fallback)
      try {
        await sendEmailVerification(userCredential.user);
      } catch (emailError: any) {
        console.error('Non-blocking error sending verification email during signup:', emailError);
        // Note: Do not throw this error to avoid aborting the signup flow
        // and leaving the user in an inconsistent state.
      }
      
      // friendLookup is created when the user sets their friend username on the Friends page (required to use Friends feature)
      
      // Store consent locally as well
      setConsentByEmail(email, consentData);
      
      // Mark user as having visited before
      localStorage.setItem('hasVisitedBefore', 'true');
      
      toast.success('Account created! Please check your email to verify your account.');
    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        toast.error('This email is already registered.');
      } else if (error.message?.includes('invite code') || error.message?.includes('Invite code')) {
        toast.error(error.message);
      } else if (error.message?.includes('Consent is required')) {
        toast.error(error.message);
      } else {
        toast.error('Failed to create account. Please try again.');
      }
      throw error;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // If you require verified email to use the app:
      if (!userCredential.user.emailVerified) {
        // Don't sign out - keep the user logged in but redirect to verification page
        throw new Error('email-not-verified');
      }

      // CRITICAL: Force refresh the ID token immediately after login so Firestore security rules
      // recognize email verification. This prevents "Missing or insufficient permissions" errors
      // that occur when contexts try to query Firestore before the token is refreshed.
      // Firestore rules check request.auth.token.email_verified, which comes from the ID token.
      try {
        await userCredential.user.getIdToken(true);
        debug.log('[AuthContext] ID token refreshed after login');
      } catch (error) {
        debug.warn('[AuthContext] Failed to refresh ID token after login:', error);
        // Non-fatal - continue with login
      }

      // Check consent status — try cache first for speed on poor network (ship WiFi).
      // Returning users will have this doc in the local Firestore IndexedDB cache.
      // Only falls back to the server on first login (no cache yet).
      const userDocRef = doc(db, 'users', userCredential.user.uid);
      let userDoc;
      try {
        const cachedDoc = await getDocFromCache(userDocRef);
        // If cache returned a non-existent doc, fall through to server fetch
        userDoc = cachedDoc.exists() ? cachedDoc : await getDoc(userDocRef);
      } catch {
        // Cache unavailable — fall back to server
        userDoc = await getDoc(userDocRef);
      }
      if (userDoc.exists()) {
        const userData = userDoc.data() as any;
        const storedConsent = userData.consentData;
        
        // If user doesn't have consent or has outdated consent, they need to consent again
        if (!storedConsent || !storedConsent.consented || storedConsent.version !== CONSENT_CONFIG.currentVersion) {
          throw new Error('consent-required');
        }
      } else {
        // User document doesn't exist, they need to consent
        throw new Error('consent-required');
      }

      // Mark user as having visited before
      localStorage.setItem('hasVisitedBefore', 'true');

      // Warm the Firestore cache right after successful login (best-effort)
      prefetchUserData(db, userCredential.user.uid).catch(() => {});

      toast.success('Logged in successfully!');
    } catch (error: any) {
      if (error.message === 'email-not-verified') {
        toast.error('Please verify your email before logging in.');
      } else if (error.message === 'consent-required') {
        toast.error('Please accept our updated privacy policy to continue.');
      } else {
        toast.error('Invalid email or password.');
      }
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    try {
      let userCredential;
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.signInWithGoogle();
        const idToken = result.credential?.idToken;
        if (!idToken) {
          throw new Error('Google Sign-In failed: No ID token returned');
        }
        const credential = GoogleAuthProvider.credential(idToken);
        userCredential = await signInWithCredential(auth, credential);
      } else {
        const provider = new GoogleAuthProvider();
        userCredential = await signInWithPopup(auth, provider);
      }

      const user = userCredential.user;
      localStorage.setItem('hasVisitedBefore', 'true');

      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (!userDocSnap.exists()) {
        await setDoc(userDocRef, {
          email: user.email ? user.email.toLowerCase() : '',
          preferredDisplayName: null,
          loyaltyStatus: [],
          consentData: null
        });
      }

      prefetchUserData(db, user.uid).catch(() => {});
      toast.success('Signed in with Google successfully!');
    } catch (error: any) {
      if (isUserCancellationError(error)) {
        debug.log('[AuthContext] Google Sign-In cancelled by user');
        toast('Google Sign-In cancelled', { icon: <Info className="h-5 w-5 text-secondary-500" /> });
      } else {
        debug.error('[AuthContext] Google Sign-In error:', error);
        if (error.code === 'auth/account-exists-with-different-credential') {
          toast.error('An account already exists with this email using a different login method. Please log in with your password first.');
        } else {
          toast.error(error.message || 'Failed to sign in with Google');
        }
      }
      throw error;
    }
  };

  const loginWithApple = async () => {
    try {
      let userCredential;
      let appleDisplayName: string | undefined;
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.signInWithApple({ skipNativeAuth: true });
        const idToken = result.credential?.idToken;
        if (!idToken) {
          throw new Error('Apple Sign-In failed: No ID token returned');
        }
        appleDisplayName = (result as any)?.user?.displayName || (result as any)?.displayName;
        const provider = new OAuthProvider('apple.com');
        const credential = provider.credential({
          idToken: idToken,
          rawNonce: result.credential?.nonce
        });
        userCredential = await signInWithCredential(auth, credential);
      } else {
        const provider = new OAuthProvider('apple.com');
        userCredential = await signInWithPopup(auth, provider);
      }

      const user = userCredential.user;
      localStorage.setItem('hasVisitedBefore', 'true');

      const nameToSet = appleDisplayName || userCredential.user.displayName;
      if (nameToSet && !user.displayName) {
        await updateProfile(user, { displayName: nameToSet }).catch(() => {});
      }

      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const isNewAccount = !userDocSnap.exists();
      if (isNewAccount) {
        await setDoc(userDocRef, {
          email: user.email ? user.email.toLowerCase() : '',
          preferredDisplayName: null,
          loyaltyStatus: [],
          consentData: null
        });
      }

      prefetchUserData(db, user.uid).catch(() => {});
      if (isNewAccount && user.email?.includes('privaterelay.appleid.com')) {
        toast.success('Signed in with Apple! (Private Relay email used. If you have an existing account, connect Apple in Profile Settings).', { duration: 6000 });
      } else {
        toast.success('Signed in with Apple successfully!');
      }
    } catch (error: any) {
      if (isUserCancellationError(error)) {
        debug.log('[AuthContext] Apple Sign-In cancelled by user');
        toast('Apple Sign-In cancelled', { icon: <Info className="h-5 w-5 text-secondary-500" /> });
      } else {
        debug.error('[AuthContext] Apple Sign-In error:', error);
        if (error.code === 'auth/account-exists-with-different-credential') {
          toast.error('An account already exists with this email using a different login method. Please log in with your password first.');
        } else {
          toast.error(error.message || 'Failed to sign in with Apple');
        }
      }
      throw error;
    }
  };

  const linkGoogleAccount = async () => {
    const userToLink = auth.currentUser || currentUser;
    if (!userToLink) throw new Error('No user logged in');
    try {
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.signInWithGoogle();
        const idToken = result.credential?.idToken;
        if (!idToken) {
          throw new Error('Google Sign-In failed: No ID token returned');
        }
        const credential = GoogleAuthProvider.credential(idToken);
        await linkWithCredential(userToLink, credential);
      } else {
        const provider = new GoogleAuthProvider();
        await linkWithPopup(userToLink, provider);
      }
      await reloadUser();
      toast.success('Google account linked successfully!');
    } catch (error: any) {
      if (isUserCancellationError(error)) {
        debug.log('[AuthContext] Linking Google account cancelled');
        toast('Linking Google account cancelled', { icon: <Info className="h-5 w-5 text-secondary-500" /> });
      } else {
        debug.error('[AuthContext] Error linking Google account:', error);
        const isConflict = error?.code === 'auth/credential-already-in-use' || error?.message?.includes('credential-already-in-use');
        if (!isConflict) {
          toast.error(error.message || 'Failed to link Google account.');
        }
      }
      throw error;
    }
  };

  const linkAppleAccount = async () => {
    const userToLink = auth.currentUser || currentUser;
    if (!userToLink) throw new Error('No user logged in');
    try {
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.signInWithApple({ skipNativeAuth: true });
        const idToken = result.credential?.idToken;
        if (!idToken) {
          throw new Error('Apple Sign-In failed: No ID token returned');
        }
        const provider = new OAuthProvider('apple.com');
        const credential = provider.credential({
          idToken: idToken,
          rawNonce: result.credential?.nonce
        });
        const userCredential = await linkWithCredential(userToLink, credential);
        if (userCredential?.user?.displayName && !userToLink.displayName) {
          await updateProfile(userToLink, { displayName: userCredential.user.displayName }).catch(() => {});
        }
      } else {
        const provider = new OAuthProvider('apple.com');
        const userCredential = await linkWithPopup(userToLink, provider);
        if (userCredential?.user?.displayName && !userToLink.displayName) {
          await updateProfile(userToLink, { displayName: userCredential.user.displayName }).catch(() => {});
        }
      }
      await reloadUser();
      toast.success('Apple account linked successfully!');
    } catch (error: any) {
      if (isUserCancellationError(error)) {
        debug.log('[AuthContext] Linking Apple account cancelled');
        toast('Linking Apple account cancelled', { icon: <Info className="h-5 w-5 text-secondary-500" /> });
      } else {
        debug.error('[AuthContext] Error linking Apple account:', error);
        const isConflict = error?.code === 'auth/credential-already-in-use' || error?.message?.includes('credential-already-in-use');
        if (!isConflict) {
          toast.error(error.message || 'Failed to link Apple account.');
        }
      }
      throw error;
    }
  };

  const unlinkAuthProvider = async (providerId: string) => {
    const userToUnlink = auth.currentUser || currentUser;
    if (!userToUnlink) throw new Error('No user logged in');

    // Reload the user to get fresh providerData from Firebase Auth server
    await userToUnlink.reload().catch(() => {});
    const activeUser = auth.currentUser || userToUnlink;

    if (activeUser.providerData.length <= 1) {
      toast.error('You cannot disconnect your only login method.');
      return;
    }
    try {
      await unlink(activeUser, providerId);
      await reloadUser();
      toast.success('Account unlinked successfully.');
    } catch (error: any) {
      debug.error('[AuthContext] Error unlinking provider:', error);
      toast.error(error.message || 'Failed to unlink account.');
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        await FirebaseAuthentication.signOut().catch(() => {});
      }
      await signOut(auth);
      setCurrentUser(null);
      setUserAnalyticsId(null);
      setPreferredDisplayName(null);
      setLoyaltyStatus([]);
      setUserConsent(null);
      setConsentRequired(false);
      setDisplayPreferences(DEFAULT_DISPLAY_PREFERENCES);
      setIsAdmin(false);
      toast.success('Logged out successfully');
    } catch (error: any) {
      toast.error('Failed to logout');
      throw error;
    }
  };

  const sendVerificationEmail = async () => {
    if (!currentUser) throw new Error('No user logged in');
    try {
      // Use auth.currentUser instead of the potentially stale currentUser state
      const userToSendEmail = auth.currentUser;
      if (!userToSendEmail) {
        throw new Error('No authenticated user found');
      }
      
      await sendEmailVerification(userToSendEmail);
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error: any) {
      toast.error('Failed to send verification email. Please try again later.');
      throw error;
    }
  };

  const reloadUser = async () => {
    if (!currentUser) return;
    try {
      const userToReload = auth.currentUser || currentUser;
      if (!userToReload) return;
      
      // Attempt to reload Firebase Auth user object
      try {
        await reload(userToReload);
        await userToReload.getIdToken(true).catch(() => {});
      } catch (reloadErr: any) {
        debug.warn('[AuthContext] Token reload notice, trying on-demand re-auth refresh...', reloadErr);
        // If token was invalidated by email change link verification, attempt silent re-authentication
        const socialProvider = userToReload.providerData.find(p => p.providerId === 'google.com' || p.providerId === 'apple.com');
        if (socialProvider) {
          await reauthenticateSocialUser(userToReload).catch(() => {});
        }
      }
      
      const updatedUser = auth.currentUser || currentUser;
      if (updatedUser) {
        setCurrentUser(updatedUser);
        const userDocRef = doc(db, 'users', updatedUser.uid);
        const userDoc = await getDoc(userDocRef).catch(() => null);
        if (userDoc && userDoc.exists()) {
          let userData = userDoc.data() as any;
          userData = await checkAndApplyEmailVerification(updatedUser, userData).catch(() => userData);
          setPreferredDisplayName(userData.preferredDisplayName ?? null);
          setLoyaltyStatus(userData.loyaltyStatus || []);
          const storedConsent = userData.consentData || null;
          setUserConsent(storedConsent);
          if (!storedConsent || !storedConsent.consented || storedConsent.version !== CONSENT_CONFIG.currentVersion) {
            setConsentRequired(true);
          } else {
            setConsentRequired(false);
          }
          setDisplayPreferences({
            ...DEFAULT_DISPLAY_PREFERENCES,
            ...(userData.displayPreferences || {})
          });
          debug.log('[AuthContext] User doc re-fetched in reloadUser');
        }
      }
    } catch (error: any) {
      debug.warn('[AuthContext] Handled reloadUser exception gracefully:', error);
    }
  };

  const updateUserDisplayName = async (displayName: string, suppressToast: boolean = false) => {
    if (!currentUser) throw new Error('No user logged in');
    try {
      // Use auth.currentUser instead of the potentially stale currentUser state
      const userToUpdate = auth.currentUser;
      if (!userToUpdate) {
        throw new Error('No authenticated user found');
      }
      
      await updateProfile(userToUpdate, { displayName });
      setCurrentUser(userToUpdate);
      
      if (!suppressToast) {
        toast.success('Name updated successfully');
      }
    } catch (error: any) {
      if (!suppressToast) {
        toast.error('Failed to update name');
      }
      throw error;
    }
  };

  const cancelPendingEmailUpdate = async () => {
    if (!currentUser) return;
    try {
      await setDoc(doc(db, 'users', currentUser.uid), { pendingEmail: deleteField() }, { merge: true });
      setPendingEmail(null);
      toast.success('Pending email verification cancelled.');
    } catch {
      toast.error('Failed to cancel pending email update.');
    }
  };

  const verifyAndRefreshEmailUpdate = async () => {
    if (!currentUser) throw new Error('No user logged in');
    const userToVerify = auth.currentUser || currentUser;
    if (!userToVerify) throw new Error('No user session found');

    try {
      // 1. Re-authenticate social SSO users to get a fresh valid token for the new email address
      const socialProvider = userToVerify.providerData.find(p => p.providerId === 'google.com' || p.providerId === 'apple.com');
      if (socialProvider) {
        await reauthenticateSocialUser(userToVerify);
      } else {
        await reload(userToVerify).catch(() => {});
      }

      // 2. Fetch fresh token and update currentUser in React state
      await userToVerify.getIdToken(true).catch(() => {});
      const activeUser = auth.currentUser || userToVerify;
      setCurrentUser(activeUser);

      // 3. Sync Firestore user profile & terms consent
      const userDocRef = doc(db, 'users', activeUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        let userData = userDoc.data() as any;
        userData = await checkAndApplyEmailVerification(activeUser, userData);
        setPreferredDisplayName(userData.preferredDisplayName ?? null);
        setLoyaltyStatus(userData.loyaltyStatus || []);
        const storedConsent = userData.consentData || null;
        setUserConsent(storedConsent);
        if (!storedConsent || !storedConsent.consented || storedConsent.version !== CONSENT_CONFIG.currentVersion) {
          setConsentRequired(true);
        } else {
          setConsentRequired(false);
        }
        setDisplayPreferences({
          ...DEFAULT_DISPLAY_PREFERENCES,
          ...(userData.displayPreferences || {})
        });
      }
    } catch (error: any) {
      if (isUserCancellationError(error)) {
        toast('Verification check cancelled', { icon: <Info className="h-5 w-5 text-secondary-500" /> });
        return;
      }
    }
  };



  const updateUserEmail = async (newEmail: string, currentPassword?: string) => {
    if (!currentUser) throw new Error('No user logged in');
    try {
      const userToUpdate = auth.currentUser;
      if (!userToUpdate) {
        throw new Error('No authenticated user found');
      }

      const normalizedNewEmail = newEmail.trim().toLowerCase();
      const oldEmail = userToUpdate.email ? userToUpdate.email.toLowerCase().trim() : null;

      if (oldEmail && oldEmail === normalizedNewEmail) {
        toast('Your account is already using this email address.', {
          icon: <Info className="h-5 w-5 text-blue-500" />,
        });
        return;
      }

      // Pre-check if target email is already registered to a different user account using friendLookup
      const lookupDocRef = doc(db, 'friendLookup', normalizedNewEmail);
      const existingLookupDoc = await getDoc(lookupDocRef);

      if (existingLookupDoc.exists()) {
        const lookupData = existingLookupDoc.data();
        if (lookupData?.uid && lookupData.uid !== userToUpdate.uid) {
          toast.error(`This email address (${normalizedNewEmail}) is already associated with another Cruising Along account.`, { duration: 6000 });
          const duplicateError = new Error('auth/email-already-in-use');
          (duplicateError as any).code = 'auth/email-already-in-use';
          throw duplicateError;
        }
      }
      
      const hasPassword = userToUpdate.providerData.some(p => p.providerId === 'password');

      if (hasPassword && currentPassword) {
        const credential = EmailAuthProvider.credential(
          userToUpdate.email!,
          currentPassword
        );
        await reauthenticateWithCredential(userToUpdate, credential);
      }

      const recordPendingEmail = async (targetEmail: string) => {
        await setDoc(doc(db, 'users', userToUpdate.uid), { pendingEmail: targetEmail }, { merge: true });
        setPendingEmail(targetEmail);
      };

      const doVerifyBeforeUpdate = async () => {
        try {
          await verifyBeforeUpdateEmail(userToUpdate, normalizedNewEmail);
        } catch (err: any) {
          if (err.code === 'auth/requires-recent-login') {
            const socialProvider = userToUpdate.providerData.find(p => p.providerId === 'google.com' || p.providerId === 'apple.com');
            if (socialProvider) {
              await reauthenticateSocialUser(userToUpdate);
              await verifyBeforeUpdateEmail(userToUpdate, normalizedNewEmail);
            } else {
              toast.error('This security change requires a recent login. Please enter your current password.');
              throw err;
            }
          } else {
            throw err;
          }
        }
      };

      await doVerifyBeforeUpdate();
      await recordPendingEmail(normalizedNewEmail);

      toast.success(`Verification link sent to ${normalizedNewEmail}. Please check your inbox, click the link, and then log back in with your updated email.`, { duration: 8000 });

      // Cleanly log out so the user re-authenticates fresh after verifying
      await logout();
    } catch (error: any) {
      if (isUserCancellationError(error)) {
        toast('Email update cancelled', { icon: <Info className="h-5 w-5 text-secondary-500" /> });
        return;
      }
      if (error.code === 'auth/wrong-password') {
        toast.error('Incorrect password');
      } else if (error.code === 'auth/email-already-in-use') {
        // Handled with specific toast above
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Invalid email address');
      } else {
        toast.error(error.message || 'Failed to update email');
      }
      throw error;
    }
  };

  const updateUserPassword = async (currentPassword: string, newPassword: string) => {
    if (!currentUser) throw new Error('No user logged in');
    try {
      // Use auth.currentUser instead of the potentially stale currentUser state
      const userToUpdate = auth.currentUser;
      if (!userToUpdate) {
        throw new Error('No authenticated user found');
      }
      
      const credential = EmailAuthProvider.credential(
        userToUpdate.email!,
        currentPassword
      );
      await reauthenticateWithCredential(userToUpdate, credential);
      await updatePassword(userToUpdate, newPassword);
      toast.success('Password updated successfully');
    } catch (error: any) {
      if (error.code === 'auth/wrong-password') {
        toast.error('Incorrect current password');
      } else {
        toast.error('Failed to update password');
      }
      throw error;
    }
  };

  const updateUserPreferredDisplayName = async (preferredDisplayName: string | null, suppressToast: boolean = false) => {
    if (!currentUser) throw new Error('No user logged in');
    try {
      await setDoc(doc(db, 'users', currentUser.uid), {
        preferredDisplayName
      }, { merge: true });
      setPreferredDisplayName(preferredDisplayName);
      
      if (!suppressToast) {
        toast.success('Display name preference updated successfully');
      }
    } catch (error: any) {
      if (!suppressToast) {
        toast.error('Failed to update display name preference');
      }
      throw error;
    }
  };

  const updateUserLoyaltyStatus = async (loyaltyStatus: UserLoyaltyStatus[]) => {
    if (!currentUser) throw new Error('No user logged in');
    try {
      await setDoc(doc(db, 'users', currentUser.uid), {
        loyaltyStatus
      }, { merge: true });
      setLoyaltyStatus(loyaltyStatus);
      toast.success('Loyalty status updated successfully');
    } catch (error: any) {
      toast.error('Failed to update loyalty status');
      throw error;
    }
  };

  const updateUserConsent = async (consentData: ConsentData) => {
    if (!currentUser) throw new Error('No user logged in');
    try {
      await setDoc(doc(db, 'users', currentUser.uid), {
        consentData
      }, { merge: true });
      setUserConsent(consentData);
      
      // Clear consent required flag if consent is valid
      if (consentData.consented && consentData.version === CONSENT_CONFIG.currentVersion) {
        setConsentRequired(false);
      }
      
      // Also store locally by email
      if (currentUser.email) {
        setConsentByEmail(currentUser.email, consentData);
      }
      
      toast.success('Privacy policy consent updated successfully');
    } catch (error: any) {
      toast.error('Failed to update consent data');
      throw error;
    }
  };

  const clearConsentRequired = () => {
    setConsentRequired(false);
  };

  const updateUserDisplayPreferences = async (newPreferences: Partial<UserDisplayPreferences>) => {
    if (!currentUser) throw new Error('No user logged in');
    try {
      // Merge new preferences with existing ones
      const updatedPreferences = {
        ...displayPreferences,
        ...newPreferences
      };
      
      await setDoc(doc(db, 'users', currentUser.uid), {
        displayPreferences: updatedPreferences
      }, { merge: true });
      
      setDisplayPreferences(updatedPreferences);
      toast.success('Display preferences updated successfully');
    } catch (error: any) {
      toast.error('Failed to update display preferences');
      throw error;
    }
  };

  const reauthenticateSocialUser = async (userToReauth: User) => {
    const appleProvider = userToReauth.providerData.find(p => p.providerId === 'apple.com');
    const googleProvider = userToReauth.providerData.find(p => p.providerId === 'google.com');

    if (Capacitor.isNativePlatform()) {
      if (appleProvider) {
        const result = await FirebaseAuthentication.signInWithApple({ skipNativeAuth: true });
        const idToken = result.credential?.idToken;
        if (idToken) {
          const provider = new OAuthProvider('apple.com');
          const credential = provider.credential({ idToken, rawNonce: result.credential?.nonce });
          await reauthenticateWithCredential(userToReauth, credential);
          return;
        }
      } else if (googleProvider) {
        const result = await FirebaseAuthentication.signInWithGoogle();
        const idToken = result.credential?.idToken;
        if (idToken) {
          const credential = GoogleAuthProvider.credential(idToken);
          await reauthenticateWithCredential(userToReauth, credential);
          return;
        }
      }
    } else {
      if (appleProvider) {
        const providerObj = new OAuthProvider('apple.com');
        await reauthenticateWithPopup(userToReauth, providerObj);
        return;
      } else if (googleProvider) {
        const providerObj = new GoogleAuthProvider();
        await reauthenticateWithPopup(userToReauth, providerObj);
        return;
      }
    }
  };

  const deleteUserAccount = async (currentPassword: string) => {
    if (!currentUser) throw new Error('No user logged in');
    
    try {
      // Step 1: Re-authenticate the user for security
      const userToDelete = auth.currentUser;
      if (!userToDelete) {
        throw new Error('No authenticated user found');
      }
      
      const hasPasswordProvider = userToDelete.providerData.some(p => p.providerId === 'password');
      if (hasPasswordProvider) {
        if (!currentPassword) {
          toast.error('Password is required to delete this account.');
          throw new Error('Password required');
        }
        const credential = EmailAuthProvider.credential(
          userToDelete.email!,
          currentPassword
        );
        await reauthenticateWithCredential(userToDelete, credential);
      }
      
      // Step 2: Delete all user's cruises using batch write
      const cruisesRef = collection(db, 'cruises');
      const cruisesQuery = query(cruisesRef, where('userId', '==', userToDelete.uid));
      const cruisesSnapshot = await getDocs(cruisesQuery);
      
      if (!cruisesSnapshot.empty) {
        const batch = writeBatch(db);
        cruisesSnapshot.docs.forEach((doc) => {
          batch.delete(doc.ref);
        });
        await batch.commit();
      }
      
      // Step 3: Delete user document and friendLookup entry from Firestore
      const userDocRef = doc(db, 'users', userToDelete.uid);
      await deleteDoc(userDocRef).catch(() => {});
      if (userToDelete.email) {
        await deleteDoc(doc(db, 'friendLookup', userToDelete.email.toLowerCase().trim())).catch(() => {});
      }
      
      // Step 4: Delete the Firebase Auth user account (re-authenticating on-demand only if session is stale)
      try {
        await deleteUser(userToDelete);
      } catch (deleteErr: any) {
        if (deleteErr.code === 'auth/requires-recent-login') {
          try {
            await reauthenticateSocialUser(userToDelete);
            await deleteUser(userToDelete);
          } catch (reauthErr: any) {
            if (isUserCancellationError(reauthErr)) {
              throw new Error('account-deletion-cancelled');
            }
            throw reauthErr;
          }
        } else {
          throw deleteErr;
        }
      }
      
      // Step 5: Clear local state
      setCurrentUser(null);
      setPreferredDisplayName(null);
      setLoyaltyStatus([]);
      setDisplayPreferences(DEFAULT_DISPLAY_PREFERENCES);
      
      // Clear any stored preferences
      localStorage.removeItem('hasVisitedBefore');
      
      toast.success('Account deleted successfully');
    } catch (error: any) {
      if (error.message === 'account-deletion-cancelled' || isUserCancellationError(error)) {
        toast('Account deletion cancelled', { icon: <Info className="h-5 w-5 text-secondary-500" /> });
        throw error;
      }
      debug.error('[AuthContext] Error deleting account:', error);
      if (error.code === 'auth/wrong-password') {
        toast.error('Incorrect password. Please try again.');
      } else if (error.code === 'auth/requires-recent-login') {
        toast.error('Please complete sign-in confirmation to delete your account.');
      } else {
        toast.error(error.message || 'Failed to delete account. Please try again.');
      }
      throw error;
    }
  };

  const value: AuthContextType = {
    currentUser,
    isAdmin,
    activeSignInProvider,
    loading,
    pendingEmail,
    cancelPendingEmailUpdate,
    verifyAndRefreshEmailUpdate,
    signup,
    login,
    loginWithGoogle,
    loginWithApple,
    linkGoogleAccount,
    linkAppleAccount,
    unlinkAuthProvider,
    logout,
    sendVerificationEmail,
    reloadUser,
    updateUserDisplayName,
    updateUserEmail,
    updateUserPassword,
    updateUserPreferredDisplayName,
    updateUserLoyaltyStatus,
    updateUserConsent,
    updateUserDisplayPreferences,
    deleteUserAccount,
    preferredDisplayName,
    loyaltyStatus,
    userConsent,
    displayPreferences,
    consentRequired,
    clearConsentRequired
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};